import type { LayerRef, MovePosition } from '../store/types';
import { resolveCfNode } from '../store/slot-schema';

/** The live DOM element for a cf-id within a frame root, via data-cf-alias fallback. */
export function liveCfElement(frameId: string, cfNodeId: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const host = document.querySelector(`[data-frame-id="${CSS.escape(frameId)}"]`);
  return host ? (resolveCfNode(host, cfNodeId) as HTMLElement | null) : null;
}

/**
 * Walk up from a DOM element to the nearest ancestor carrying a stable
 * `data-cf-id`, provided that node lives inside a frame root (`data-frame-id`).
 * Returns null if the element is not inside a frame or no cf-id is found.
 * Pure (no layout) so it is unit-testable in jsdom.
 */
export function layerRefFromElement(start: Element | null): LayerRef | null {
  let el: Element | null = start;
  let cfNodeId: string | null = null;
  while (el) {
    if (cfNodeId === null && el.hasAttribute('data-cf-id')) {
      cfNodeId = el.getAttribute('data-cf-id');
    }
    const frameId = el.getAttribute('data-frame-id');
    if (frameId) return cfNodeId ? { frameId, cfNodeId } : null;
    el = el.parentElement;
  }
  return null;
}

/**
 * Resolve a viewport point (client coords) to the layer under it. Works in
 * both render modes: the source DOM is always laid out, and in canvas mode its
 * transform is synced to painted pixels, so elementFromPoint aligns with what
 * the user sees.
 */
type RectOf = (el: Element) => DOMRect;
const liveRect: RectOf = (el) => el.getBoundingClientRect();

function rectContains(r: DOMRect, x: number, y: number): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * Geometry walk: among the `[data-cf-id]` descendants of `root` whose rect
 * contains (x,y), return the DEEPEST — the match with no other match nested
 * inside it. Overlapping non-nested matches (siblings) are disambiguated by
 * smallest area. Pure modulo `rectOf` (injected in tests; live layout by
 * default), so unit-testable without a real layout engine. This is the
 * fallback for unified mode, where one big `<canvas layoutsubtree>` paints all
 * frames and `elementFromPoint` would return the bitmap, not the source node.
 */
export function deepestLayerAt(root: Element, x: number, y: number, rectOf: RectOf = liveRect): HTMLElement | null {
  const matches: Element[] = [];
  if (root.hasAttribute('data-cf-id') && rectContains(rectOf(root), x, y)) matches.push(root);
  for (const el of Array.from(root.querySelectorAll('[data-cf-id]'))) {
    if (rectContains(rectOf(el), x, y)) matches.push(el);
  }
  if (matches.length === 0) return null;
  // Deepest = a match with no other match nested inside it.
  const leaves = matches.filter((m) => !matches.some((o) => o !== m && m.contains(o)));
  const pool = leaves.length ? leaves : matches;
  const pick = pool.reduce((best, el) => {
    const r = rectOf(el);
    const b = rectOf(best);
    return r.width * r.height < b.width * b.height ? el : best;
  });
  return pick as HTMLElement;
}

/**
 * Geometry walk: the smallest `[data-frame-id]` root whose rect contains (x,y).
 * `scope` defaults to the live document; tests pass a host element. Used to
 * locate the owning frame before walking its layers in unified mode.
 */
export function frameRootAt(x: number, y: number, rectOf: RectOf = liveRect, scope?: ParentNode): HTMLElement | null {
  const where: ParentNode | null = scope ?? (typeof document !== 'undefined' ? document : null);
  if (!where) return null;
  let best: Element | null = null;
  let bestArea = Infinity;
  for (const el of Array.from(where.querySelectorAll('[data-frame-id]'))) {
    const r = rectOf(el);
    if (!rectContains(r, x, y)) continue;
    const area = r.width * r.height;
    if (area < bestArea) { best = el; bestArea = area; }
  }
  return (best as HTMLElement) ?? null;
}

/**
 * Resolve a viewport point (client coords) to the layer under it.
 *
 * - `dom`/`canvas` (default): the source DOM is laid out and (in canvas mode)
 *   its transform is synced to painted pixels, so `elementFromPoint` aligns
 *   with what the user sees.
 * - `unified`: one big `<canvas layoutsubtree>` paints every frame, so
 *   `elementFromPoint` returns the bitmap. Try `elementsFromPoint` (plural) —
 *   if Canary surfaces the layoutsubtree children we take the first that yields
 *   a layer (skipping the canvas). Otherwise fall back to the geometry walk.
 *   The painted bitmap is never returned as a layer.
 */
export function resolveLayerAt(clientX: number, clientY: number, mode: 'dom' | 'canvas' | 'unified' = 'dom'): LayerRef | null {
  if (typeof document === 'undefined') return null;
  if (mode !== 'unified') {
    return layerRefFromElement(document.elementFromPoint(clientX, clientY));
  }
  // Unified: see through the painted bitmap.
  const stack = typeof document.elementsFromPoint === 'function'
    ? document.elementsFromPoint(clientX, clientY)
    : [];
  for (const el of stack) {
    if (el instanceof HTMLCanvasElement) continue; // the layoutsubtree bitmap
    const ref = layerRefFromElement(el);
    if (ref) return ref;
  }
  // Geometry fallback: find the owning frame, then its deepest layer.
  const frameRoot = frameRootAt(clientX, clientY);
  if (!frameRoot) return null;
  const deepest = deepestLayerAt(frameRoot, clientX, clientY);
  return deepest ? layerRefFromElement(deepest) : null;
}

/**
 * Read the outerHTML of one layer from a frame's display HTML, by cf-id.
 * Mirrors the querySelector path used by `main.extractSubtree`. Pure: parses
 * into a detached template so it is safe in tests and off the live DOM.
 */
export function subtreeHtml(displayHtml: string, cfNodeId: string): string | null {
  if (typeof document === 'undefined') return null;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = displayHtml;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);
  const target = resolveCfNode(host, cfNodeId);
  return target ? target.outerHTML : null;
}

export interface Crumb {
  cfNodeId: string;
  tag: string;
}

/**
 * One step out: the cf-id of the crumb immediately enclosing `cfNodeId` in the
 * breadcrumb chain. Null at the outermost crumb (no enclosing layer → step out
 * to frame level) or when the id is not in the chain.
 */
export function parentCrumb(crumbs: Crumb[], cfNodeId: string): string | null {
  const i = crumbs.findIndex((c) => c.cfNodeId === cfNodeId);
  return i > 0 ? crumbs[i - 1].cfNodeId : null;
}

/**
 * The first descendant layer (nearest cf-id-bearing child) inside `cfNodeId`,
 * for drill-in (ArrowDown stepping). Null when the layer has no cf-id-bearing
 * descendants. Parsed from a detached template, so pure/test-safe.
 */
export function firstChildLayer(displayHtml: string, cfNodeId: string): string | null {
  if (typeof document === 'undefined') return null;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = displayHtml;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);
  const node = resolveCfNode(host, cfNodeId);
  const child = node?.querySelector('[data-cf-id]');
  return child ? child.getAttribute('data-cf-id') : null;
}

/**
 * The previous/next cf-id-bearing sibling of `cfNodeId` within the same parent,
 * for left/right sibling stepping. Skips any siblings without a cf-id. Null at
 * the ends or when the id is absent. Parsed from a detached template.
 */
export function siblingLayer(displayHtml: string, cfNodeId: string, dir: 'prev' | 'next'): string | null {
  if (typeof document === 'undefined') return null;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = displayHtml;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);
  const node = resolveCfNode(host, cfNodeId);
  if (!node) return null;
  let sib = dir === 'next' ? node.nextElementSibling : node.previousElementSibling;
  while (sib && !sib.hasAttribute('data-cf-id')) {
    sib = dir === 'next' ? sib.nextElementSibling : sib.previousElementSibling;
  }
  return sib ? sib.getAttribute('data-cf-id') : null;
}

/**
 * The chain of cf-id-bearing ancestors from the outermost down to the target
 * (inclusive), for the selection breadcrumb. Parsed from a detached template.
 */
export function ancestorChain(displayHtml: string, cfNodeId: string): Crumb[] {
  if (typeof document === 'undefined') return [];
  const tmpl = document.createElement('template');
  tmpl.innerHTML = displayHtml;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);
  const target = resolveCfNode(host, cfNodeId);
  if (!target) return [];
  const out: Crumb[] = [];
  let el: Element | null = target;
  while (el && el !== host) {
    const id = el.getAttribute('data-cf-id');
    if (id) out.unshift({ cfNodeId: id, tag: el.tagName.toLowerCase() });
    el = el.parentElement;
  }
  return out;
}

/**
 * Pure: pointer Y within a candidate's rect → drop position. Top 30% = before,
 * bottom 30% = after, middle 40% = inside (only when the candidate may hold
 * children); for a non-container the middle resolves to the nearest edge.
 */
export function dropPosition(rect: DOMRect, y: number, isContainer: boolean): MovePosition {
  const frac = (y - rect.top) / (rect.height || 1);
  if (frac < 0.3) return 'before';
  if (frac > 0.7) return 'after';
  if (isContainer) return 'inside';
  return frac < 0.5 ? 'before' : 'after';
}
